import { useCallback, useEffect, useMemo } from 'react';
import {
  Alarm,
  AlarmsOrderByInput,
  GetAlarmsQuery,
  GetAlarmsQueryVariables,
  SortOrder,
  useGetAlarmsQuery,
} from '../../gql/generated';
import { AlarmFilters, useAlarmFilters } from '../../hooks/useAlarmFilters';
import { useTableState } from '../../hooks/useTableState';
import { buildAlarmRows, filterAlarmRows } from './alarmRows';

export function useAlarmsTable() {
  // Use the centralized filter hook for filter state
  const { filters, showAllUsers } = useAlarmFilters();

  // Only the dataset scope goes to the server. The name/machine/signal filters
  // run client-side over the folded rows: the whole list is fetched anyway, and
  // a server-filtered list would fold groups from a subset of their members,
  // so a group action or panel would silently miss the filtered-out signals.
  const serverFilter = useMemo(
    () => (showAllUsers ? { allUsers: true } : {}),
    [showAllUsers]
  );

  const tableState = useTableState<
    Alarm,
    AlarmFilters,
    GetAlarmsQuery,
    GetAlarmsQueryVariables
  >({
    useQuery: useGetAlarmsQuery,
    dataAccessor: (data) => data?.alarms.rows ?? [],
    graphQLFilter: serverFilter,
    externalFilters: filters,
    buildOrderBy: (sorting): AlarmsOrderByInput => {
      if (sorting.length === 0) return {};

      const firstSort = sorting[0];

      // Map column IDs to GraphQL field names
      const columnIdToFieldMap: Record<string, string> = {
        alarmName: 'name',
        signal: 'signal',
      };

      const fieldName = columnIdToFieldMap[firstSort.id] || firstSort.id;

      return {
        [fieldName]: firstSort.desc ? SortOrder.desc : SortOrder.asc,
      } as AlarmsOrderByInput;
    },
    localStoragePrefix: 'alarms',
    defaultPageSize: 20,
    // The members of a grouped alert fold into one row, and a server page can
    // split them, so the whole list arrives at once and is paginated here.
    fetchAllRows: true,
  });

  // Every alert in scope, unfiltered, so a deep link resolves a group row even
  // while the filters would hide it from the list.
  const allRows = useMemo(
    () => buildAlarmRows(tableState.data),
    [tableState.data]
  );

  const rows = useMemo(
    () => filterAlarmRows(allRows, filters),
    [allRows, filters]
  );

  // A grouped alert counts once, the way the table shows it
  const totalCount = rows.length;

  const { pageIndex, pageSize } = tableState.pagination;

  const totalPages = useMemo(
    () => (totalCount > 0 ? Math.ceil(totalCount / pageSize) : 0),
    [totalCount, pageSize]
  );

  const pageRows = useMemo(
    () => rows.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize),
    [rows, pageIndex, pageSize]
  );

  const selectedAlarmIds = useMemo(
    () =>
      Object.keys(tableState.rowSelection).filter(
        (id) => tableState.rowSelection[id]
      ),
    [tableState.rowSelection]
  );

  const setPageIndex = useCallback(
    (nextPageIndex: number) => {
      tableState.onPaginationChange({
        pageIndex: nextPageIndex,
        pageSize: tableState.pagination.pageSize,
      });
    },
    [tableState]
  );

  const setPageSize = useCallback(
    (nextPageSize: number) => {
      tableState.onPaginationChange({
        pageIndex: 0,
        pageSize: nextPageSize,
      });
    },
    [tableState]
  );

  // A stale ?page= from an earlier, longer list would render an empty page,
  // so the index clamps to the last page that exists.
  useEffect(() => {
    if (tableState.loading) return;
    if (totalPages > 0 && pageIndex >= totalPages) {
      setPageIndex(totalPages - 1);
    }
  }, [tableState.loading, totalPages, pageIndex, setPageIndex]);

  const setSelectedAlarmIds = useCallback(
    (ids: string[]) => {
      const newSelection = ids.reduce(
        (acc, id) => ({ ...acc, [id]: true }),
        {}
      );
      tableState.onRowSelectionChange(newSelection);
    },
    [tableState]
  );

  return {
    ...tableState,
    rows: pageRows,
    // Every page at once and unfiltered, so a deep link resolves an alert off
    // the open page and outside the active filters.
    allRows,
    refetch: tableState.refetch,
    totalCount,
    totalPages,
    pageIndex,
    pageSize,
    filters,
    setPageIndex,
    setPageSize,
    selectedAlarmIds,
    setSelectedAlarmIds,
    setSorting: tableState.onSortingChange,
    setColumnVisibility: tableState.onColumnVisibilityChange,
    setColumnOrder: tableState.onColumnOrderChange,
    setColumnPinning: tableState.onColumnPinningChange,
  };
}
